import "./wdyr";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import "./index.css";
import "./theme/tokens.css";
// Tailwind is precompiled by `tw:build` (npm prebuild/prestart) into
// tailwind.generated.css — CRA/CRACO's postcss-loader does not run the
// tailwindcss plugin, so importing the raw `tailwind.css` (with @tailwind
// directives) produced no utilities. Import the generated output instead.
import "./tailwind.generated.css";
import App from "./App";
import Auth0ProviderWithHistory from "./pages/Auth/Auth0ProviderWithHistory";
import { ThemeProvider } from "@mui/material/styles";
import TagManager, { TagManagerArgs } from "react-gtm-module";
import {
  APP_BACKGROUND_COLOR,
  APP_COLOR,
  darkTheme,
  FONT_FAMILY,
  TEXT_COLOR,
} from "./theme";
import * as Sentry from "@sentry/react";
import { replayIntegration } from "@sentry/react";

const tagManagerArgs: TagManagerArgs = {
  gtmId: "GTM-KBJ86GPC",
};

if (process.env.REACT_APP_ENV === "production") {
  TagManager.initialize(tagManagerArgs);

  Sentry.init({
    dsn: "https://cfded10beb7a4289a90b0db485c8c301@o4504800631259136.ingest.sentry.io/4504800632438784",
    integrations: [
      Sentry.browserTracingIntegration(),
      replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

if (document.body && document.body.style) {
  document.body.style.color = TEXT_COLOR;
  document.body.style.backgroundColor = APP_BACKGROUND_COLOR;
  document.body.style.fontFamily = FONT_FAMILY;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 2, // preserve prior resilience (was retry:2 before this phase) — zero behavioral change
      refetchOnWindowFocus: false,
    },
  },
});

// @tanstack/query-devtools probes Intl.Locale(navigator.language) at init; in
// locale-less browsers (headless/CI) that throws a RangeError and takes the whole
// dev tree down via the CRA overlay — skip mounting devtools there.
const devtoolsLocaleSafe = (() => {
  try {
    const LocaleCtor = (Intl as unknown as { Locale?: new (tag: string) => unknown }).Locale;
    if (!LocaleCtor) return false;
    new LocaleCtor(navigator.language);
    return true;
  } catch {
    return false;
  }
})();

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

const root = createRoot(rootElement);
root.render(
  <QueryClientProvider client={queryClient}>
  {process.env.NODE_ENV === "development" && devtoolsLocaleSafe && (
    <ReactQueryDevtools initialIsOpen={false} />
  )}
  <ThemeProvider theme={darkTheme}>
    <BrowserRouter>
      <Auth0ProviderWithHistory>
        <App />
        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          draggable
          toastStyle={{ backgroundColor: APP_COLOR, color: "black" }}
        />
      </Auth0ProviderWithHistory>
    </BrowserRouter>
  </ThemeProvider>
  </QueryClientProvider>
);
